import {
	type ArtemisAuditLogFilterInput,
	type ArtemisAuditLogSortInput,
	type AuditAction,
	GetAuditLogsDocument,
} from "@/common/artemis-sdk/graphql";
import type { AuditLog } from "../model";
import { useGqlClient } from "../use-gql-client";
import { toOrder, usePagedList } from "./use-paged-list";

/** UI-facing audit filter state; translated to a server `where` by {@link buildAuditWhere}. */
export type AuditFilters = {
	workspaceId?: string;
	action?: AuditAction;
	/** Exact-match against the audited entity's type (e.g. "Project"). */
	entityType?: string;
	/** Free-text match against entity id or type (server-side `contains`). */
	search?: string;
};

/**
 * Auditable entity types — the CLR entity class name minus its `Artemis` prefix, matching what the
 * backend audit interceptor writes (see audit-interceptor.cs `EntityTypeName`). Keep in sync when a
 * new audited entity is added.
 */
export const AUDIT_ENTITY_TYPES = [
	"Agent",
	"Approval",
	"Asset",
	"CredentialGrant",
	"CredentialRequest",
	"Deployment",
	"Report",
	"Environment",
	"Job",
	"JobRun",
	"Organization",
	"OrganizationAdmin",
	"OrganizationDomain",
	"OrganizationQuota",
	"OrganizationSsoConfig",
	"Project",
	"Task",
	"User",
	"Workspace",
	"WorkspaceMember",
] as const;

/** Builds a server-side `where` from the UI filter state, or undefined when no filters are set. */
export function buildAuditWhere(
	f: AuditFilters,
): ArtemisAuditLogFilterInput | undefined {
	const and: ArtemisAuditLogFilterInput[] = [];
	if (f.workspaceId) and.push({ workspaceId: { eq: f.workspaceId } });
	if (f.action) and.push({ action: { eq: f.action } });
	if (f.entityType) and.push({ entityType: { eq: f.entityType } });
	if (f.search) {
		and.push({
			or: [
				{ entityId: { contains: f.search } },
				{ entityType: { contains: f.search } },
			],
		});
	}
	return and.length ? { and } : undefined;
}

/**
 * Server-side paginated audit trail: Next/Prev cursor pagination, sort (newest first by default) and
 * the {@link AuditFilters} `where`. Pass `workspaceId` to scope to a single workspace — this is the
 * authorization boundary the backend enforces, so a workspace admin can only read their own
 * workspace's trail; omit it for the org-wide trail (org-admin only).
 */
export function useAuditLogsPaged(workspaceId?: string) {
	const gqlFetch = useGqlClient();

	return usePagedList<AuditLog>({
		baseKey: ["auditLogs", workspaceId ?? null],
		initialSort: [{ id: "occurredAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetAuditLogsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisAuditLogSortInput>(sort),
				where: buildAuditWhere({ ...filters, search: debouncedSearch }),
			});
			const c = data.auditLogs;
			return {
				nodes: (c?.nodes as AuditLog[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}
